<template>
  <Dialog
    v-model:visible="visibleDetails"
    modal
    position="top"
    :header="headerDetails"
    class="w-full h-full"
  >
    <Chart
      v-if="showDetails"
      type="bar"
      :data="chartDataDetails"
      :options="chartOptionsDetails"
      :style="{ height: `${45 * chartDataDetails.datasets[0].data.length}px` }"
    />
    <div
      v-else
      class="flex flex-1 w-full h-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
  </Dialog>
  <div>
    <div class="flex flex-row justify-between items-center ">
      <h1 class="font-semibold leading-none">
        DML Activities
      </h1>
      <FloatLabel
        style="width: 50%"
        variant="on"
      >
        <Select
          v-model="object"
          class="w-full"
          size="small"
          filter
          :options="props.objects"
          input-id="objects-label"
          option-label="label"
          option-value="value"
        />
        <label for="objects-label">Object</label>
      </FloatLabel>
    </div>

    <div class="flex flex-col lg:flex-row py-3 gap-4 justify-end">
      <SelectButton
        v-model="timeFrame"
        class="flex justify-center w-full"
        option-label="label"
        option-value="value"
        :options="timeFrames"
        :allow-empty="true"
        @change="$emit('change-time-frame',timeFrame)"
      />
    </div>
    <Chart
      v-if="Object.keys(syncStats).length"
      type="bar"
      :data="chartData"
      :options="chartOptions"
      class="h-[20rem]"
    />
    <div
      v-else
      class="flex flex-1 w-full h-[20rem] items-center justify-center"
    >
      <ProgressSpinner />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useDark } from '@vueuse/core'

const darkMode = useDark()
const syncStats = defineModel({ type: Object })
defineEmits(['change-time-frame'])
const props = defineProps({
  objects: {
    type: Array,
    default () {
      return []
    }
  },
  rows: {
    type: Array,
    default () {
      return []
    }
  }
})
const timeFrames = [{ label: 'Hour', value: 'hourly' }, { label: 'Day', value: 'daily' }, { label: 'Week', value: 'weekly' }]
const timeFrame = ref('weekly')
const object = ref('all')
const visibleDetails = ref(false)
const showDetails = ref(false)
const chartDataDetails = ref()
const headerDetails = ref('')

let labelsMap = {}
const filtersSyncRowChart = {
  inserted: { name: 'Inserted', color: '--p-green-500' },
  updated: { name: 'Updated', color: '--p-orange-400' },
  deleted: { name: 'Deleted', color: '--p-red-500' }
}

watch(
  timeFrame,
  (newValue, oldValue) =>
    newValue === null && setTimeout(() => (timeFrame.value = oldValue), 0)
)

const injectCSS = css => {
  const el = document.createElement('style')
  el.innerText = css
  document.head.appendChild(el)
  return el
}

const chartData = computed(() => {
  const stats = syncStats.value
  labelsMap = {}
  const datasetsMap = {}
  const sumDatasetMap = {}
  // Stats come ordered by object and time frame
  stats.syncStats && stats.syncStats.forEach((item) => {
    labelsMap[item.starttime] = true
    if (!datasetsMap[item.object]) {
      datasetsMap[item.object] = []
    }
    if (!sumDatasetMap[item.starttime]) {
      sumDatasetMap[item.starttime] = { inserted: 0, updated: 0, deleted: 0 }
    }
    datasetsMap[item.object].push({ inserted: item.newrowcount, updated: item.editrowcount, deleted: item.deleterowcount })
    sumDatasetMap[item.starttime].inserted += item.newrowcount
    sumDatasetMap[item.starttime].updated += item.editrowcount
    sumDatasetMap[item.starttime].deleted += item.deleterowcount
  })

  const datasets = { all: getDatasets(Object.values(sumDatasetMap)) }
  Object.keys(datasetsMap).forEach(object => {
    datasets[object] = getDatasets(datasetsMap[object])
  })

  return {
    labels: Object.keys(labelsMap).map(el => formatDate(el, stats.timeFrame)),
    datasets: Object.values(datasets[object.value]),
    responsive: true
  }
})

const getDatasets = (inputData) => {
  return inputData.reduce((acc, el) => {
    Object.keys(acc).forEach(k => { acc[k].data.push(el[k]) })
    return acc
  }, getEmptyDatasets())
}

const getEmptyDatasets = () => {
  return Object.keys(filtersSyncRowChart).reduce((acc, el) => {
    const documentStyle = getComputedStyle(document.documentElement)
    acc[el] = { label: filtersSyncRowChart[el].name, data: [], type: 'bar', backgroundColor: documentStyle.getPropertyValue(filtersSyncRowChart[el].color) }
    return acc
  }, {})
}

const chartOptions = computed(() => {
  return {
    stacked: false,
    maintainAspectRatio: false,
    aspectRatio: 0.6,
    responsive: true,
    onHover: (event, clickedElements) => {
      if (!clickedElements.length || object.value !== 'all') {
        injectCSS('body { cursor: default }')
      } else {
        injectCSS('body { cursor: pointer }')
      }
    },
    onClick: async (event, clickedElements) => {
      if (clickedElements[0] && object.value === 'all') {
        showDetails.value = false
        visibleDetails.value = true
        const stats = syncStats.value
        labelsMap = {}
        stats.syncStats && stats.syncStats.forEach((item) => {
          labelsMap[item.starttime] = true
        })
        const params = [{ field: 'newrowcount', header: 'Inserted', color: '--p-green-500' }, { field: 'editrowcount', header: 'Updated', color: '--p-orange-400' }, { field: 'deleterowcount', header: 'Deleted', color: '--p-red-500' }]
        const label = Object.keys(labelsMap)[clickedElements[0].index]
        const data = stats.syncStats.filter(el => el.starttime === label && el[params[clickedElements[0].datasetIndex].field] > 0).map(el => ({ label: el.object, count: el[params[clickedElements[0].datasetIndex].field] })).sort((a, b) => b.count - a.count)
        headerDetails.value = params[clickedElements[0].datasetIndex].header + ' - ' + formatDate(label, timeFrame.value)
        const documentStyle = getComputedStyle(document.documentElement)
        chartDataDetails.value = {
          type: 'bar',
          labels: data.map(el => el.label),
          datasets: [
            {
              data: data.map(el => el.count),
              backgroundColor: documentStyle.getPropertyValue(params[clickedElements[0].datasetIndex].color),
            },
          ]
        }
        showDetails.value = true
      }
    },
    plugins: {
      legend: {
        labels: darkMode.value
          ? {
              color: 'rgb(204, 204, 204)'
            }
          : undefined,
        position: 'top',
        onHover: () => {
          injectCSS('body { cursor: pointer }')
        },
        onLeave: () => {
          injectCSS('body { cursor: default }')
        },
      },
      tooltip: {
        enabled: true,
        xAlign: 'left',
        yAlign: 'center',
        callbacks: {
          footer: () => {
            return object.value === 'all' ? 'Click for details' : undefined
          },
        }
      },
    },
    scales: darkMode.value
      ? {
          x: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          },
          y: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          }
        }
      : undefined
  }
})

const chartOptionsDetails = computed(() => {
  return {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    barPercentage: 0.9,
    categoryPercentage: 0.7,
    plugins: {
      legend: {
        display: false,
      }
    },
    scales: darkMode.value
      ? {
          x: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          },
          y: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          }
        }
      : undefined
  }
})

const formatDate = (date, timeFrame) => {
  const d = new Date(date)
  if (timeFrame === 'daily') {
    return d.toLocaleTimeString('en-IT', { weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: true })
  } else if (timeFrame === 'weekly') {
    return d.toLocaleDateString('en-IT', { weekday: 'short', month: 'short', day: 'numeric' })
  } else if (timeFrame === 'hourly') {
    return d.toLocaleTimeString('en-IT', { hour: 'numeric', minute: 'numeric', hour12: true })
  }
}
</script>
